import { Link } from 'react-router-dom'
import { TruncatedValue } from './TruncatedValue'
import { IsGranted, useAuth } from '../modules/auth'
import { ClientSnapshotIcon } from './client-snapshot/ClientSnapshotIcon'
import { CLIENT_SNAPSHOT_ROLES } from './client-snapshot/core/_models'

type Props = {
  /** ID клиента у мерчанта — единственный идентификатор клиента, показываемый в таблицах. */
  externalId?: string | number | null
  /** Карточка клиента открывается только по токену (/client/info/{token}); без него ссылки нет. */
  token?: string | null
  /** Депозит строки — снимок клиента считает оценку для него. */
  paymentTicketToken?: string | null
  className?: string
}

/**
 * The «Клиент» value outside a table (a card field, a window): the same external id linking to the
 * client card and the same client snapshot as the list cell.
 */
export const ClientValue = ({ externalId, token, paymentTicketToken }: Omit<Props, 'className'>) => {
  const { currentUser } = useAuth()
  const hasValue = externalId !== null && externalId !== undefined && externalId !== ''
  const value = hasValue ? String(externalId) : null
  const showSnapshot = !!token && IsGranted(CLIENT_SNAPSHOT_ROLES, currentUser)

  if (value === null) {
    return <>-</>
  }

  return (
    <span className='badge'>
      {token ? (
        <Link to={'/client/info/' + token} className='text-primary' target='_blank'>
          {/* Внутри ссылки свёрнутое значение не должно быть отдельной точкой фокуса. */}
          <TruncatedValue value={value} focusable={false} />
        </Link>
      ) : (
        <TruncatedValue value={value} />
      )}
      {showSnapshot && <ClientSnapshotIcon token={token} paymentTicketToken={paymentTicketToken} />}
    </span>
  )
}

/**
 * Ячейка «Клиент» списков и отчётов: внешний ID клиента ссылкой на его карточку.
 *
 * Значение всегда одно — второй идентификатор того же клиента (внутренний номер, токен) в
 * таблицу не выводится. Длинный ID сворачивается `TruncatedValue`: длину задаёт мерчант
 * (до 255 символов), а сплошная строка без пробелов растягивает таблицу за край экрана.
 */
export const ClientCell = ({ className, ...value }: Props) => (
  <td className={className}>
    <ClientValue {...value} />
  </td>
)

export default ClientCell
